import { useEffect, useRef, useState } from 'react';
import {
  collection, query, where, onSnapshot,
  addDoc, deleteDoc, updateDoc, doc, serverTimestamp,
} from 'firebase/firestore';
import {
  ref, uploadBytesResumable, getDownloadURL, deleteObject,
} from 'firebase/storage';
import { db, storage } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function FileIcon({ mimeType }) {
  const isImage = mimeType?.startsWith('image/');
  const isPdf = mimeType === 'application/pdf';
  if (isPdf) return <span className="text-dr font-bold text-[11px]">PDF</span>;
  if (isImage) return <span className="text-pb font-bold text-[11px]">IMG</span>;
  return <span className="text-[#9CA3AF] font-bold text-[11px]">DOC</span>;
}

export default function DocumentUpload({ requestId }) {
  const { currentUser } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const fileInputRef = useRef();

  useEffect(() => {
    const q = query(
      collection(db, 'documents'),
      where('requestId', '==', requestId)
    );
    const unsub = onSnapshot(q, (snap) => {
      const docs = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.createdAt?.seconds ?? 0) - (b.createdAt?.seconds ?? 0));
      setDocuments(docs);
      setLoading(false);
    });
    return unsub;
  }, [requestId]);

  async function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    // 20 MB limit
    if (file.size > 20 * 1024 * 1024) {
      setError('File must be under 20 MB.');
      return;
    }
    setError('');
    setUploading(true);
    setProgress(0);

    const storagePath = `documents/${requestId}/${Date.now()}_${file.name}`;
    const storageRef = ref(storage, storagePath);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        setProgress(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100));
      },
      (err) => {
        console.error(err);
        setError('Upload failed. Please try again.');
        setUploading(false);
      },
      async () => {
        const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
        await addDoc(collection(db, 'documents'), {
          requestId,
          name: file.name,
          storagePath,
          downloadUrl,
          mimeType: file.type,
          size: file.size,
          uploadedByEmail: currentUser.email,
          visibleToCustomer: false,
          createdAt: serverTimestamp(),
        });
        setUploading(false);
        setProgress(0);
        // Reset file input
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    );
  }

  async function toggleCustomerVisibility(document) {
    await updateDoc(doc(db, 'documents', document.id), {
      visibleToCustomer: !document.visibleToCustomer,
    });
  }

  async function handleDelete(document) {
    if (!window.confirm(`Delete "${document.name}"?`)) return;
    try {
      await deleteObject(ref(storage, document.storagePath));
    } catch (err) {
      // File may already be gone from storage
      console.warn('Storage delete failed:', err);
    }
    await deleteDoc(doc(db, 'documents', document.id));
  }

  return (
    <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-6">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">Documents</h3>
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="text-[12px] font-medium text-pb hover:text-navy border border-pb/20 hover:border-pb/40 px-3 py-1.5 rounded-[8px] transition disabled:opacity-50"
        >
          + Upload file
        </button>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={handleFileChange}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.csv,.txt"
        />
      </div>

      {uploading && (
        <div className="mb-4">
          <div className="flex items-center justify-between text-[12px] text-[#6B7280] mb-1">
            <span>Uploading…</span>
            <span>{progress}%</span>
          </div>
          <div className="w-full bg-[#E5E7EB] rounded-full h-1.5">
            <div
              className="bg-pb h-1.5 rounded-full transition-all duration-200"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {error && <p className="text-[12px] text-dr mb-3">{error}</p>}

      {loading ? (
        <p className="text-[13px] text-[#9CA3AF]">Loading…</p>
      ) : documents.length === 0 ? (
        <p className="text-[13px] text-[#9CA3AF]">No documents yet. Upload files to attach them to this request.</p>
      ) : (
        <div className="space-y-2">
          {documents.map((d) => (
            <div key={d.id} className="flex items-center gap-3 p-3 rounded-[8px] bg-surface border border-[#F3F4F6] group">
              <div className="w-10 h-10 bg-white border border-[#E5E7EB] rounded-[8px] flex items-center justify-center flex-shrink-0">
                <FileIcon mimeType={d.mimeType} />
              </div>
              <div className="flex-1 min-w-0">
                <a
                  href={d.downloadUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[13px] font-medium text-[#111827] hover:text-pb truncate block"
                >
                  {d.name}
                </a>
                <p className="text-[12px] text-[#9CA3AF]">
                  {formatBytes(d.size)} · {d.uploadedByEmail}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => toggleCustomerVisibility(d)}
                  title={d.visibleToCustomer ? 'Visible to customer — click to hide' : 'Hidden from customer — click to share'}
                  className={`text-[11px] font-medium px-2.5 py-1 rounded-[12px] border transition ${
                    d.visibleToCustomer
                      ? 'bg-green-light text-[#065F46] border-sg/20 hover:border-sg/40'
                      : 'bg-surface text-[#9CA3AF] border-[#E5E7EB] hover:border-[#D1D5DB]'
                  }`}
                >
                  {d.visibleToCustomer ? 'Shared' : 'Staff only'}
                </button>
                <button
                  onClick={() => handleDelete(d)}
                  className="text-[12px] text-[#D1D5DB] hover:text-dr opacity-0 group-hover:opacity-100 transition"
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
